import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@mui/material";
import { get, post } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface Country {
  id: number;
  name: string;
}

interface Vendor {
  vendorId: string;
  vendorName: string;
}

interface AddStateProps {
  handleClose: () => void;
  fetchStates: () => void;
  selectedVendorId: string; // Add this prop
}

const BoardAddState: React.FC<AddStateProps> = ({
  handleClose,
  fetchStates,
  selectedVendorId // Receive the selected vendor ID
}) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    vendorId: selectedVendorId || "", // Initialize with passed vendor ID
    countryId: "",
    name: "",
    shortCode: "",
    isActive: true
  });

  /* =========================
     Fetch Vendors (for display purposes only)
  ========================= */
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await get(`${APIURL}/api/vendors`);
        setVendors(res?.data || res);
      } catch (error) {
        showSnackbar("Failed to load vendors", "error");
      }
    };

    fetchVendors();
  }, [APIURL, showSnackbar]);

  /* =========================
     Update form when selectedVendorId prop changes
  ========================= */
  useEffect(() => {
    setForm(prev => ({ ...prev, vendorId: selectedVendorId }));
  }, [selectedVendorId]);

  /* =========================
     Fetch Countries (Vendor Based)
  ========================= */
  useEffect(() => {
    const fetchCountries = async () => {
      if (!selectedVendorId) {
        setCountries([]);
        return;
      }

      try {
        const res = await get(
          `${APIURL}/api/miscellaneous/country/vendor/${selectedVendorId}/findAll`
        );
        setCountries(res?.data || res || []);
      } catch (error) {
        showSnackbar("Failed to load countries", "error");
      }
    };

    fetchCountries();
  }, [APIURL, selectedVendorId, showSnackbar]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.vendorId || !form.countryId || !form.name) {
      showSnackbar("Vendor, Country and State Name are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        vendorId: form.vendorId,
        countryId: Number(form.countryId),
        name: form.name,
        shortCode: form.shortCode || null,
        isActive: form.isActive
      };

      const response = await post(
        `${APIURL}/api/miscellaneous/state`,
        payload
      );

      if (response?.statusCode === "200") {
        showSnackbar(response?.message, "success");
        fetchStates();
        handleClose();
      } else {
        showSnackbar(response?.message || "Failed to add state", "error");
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  // Find the selected vendor name for display
  const selectedVendor = vendors.find(v => v.vendorId === selectedVendorId);

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Add State
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor Display - Read Only */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="vendor-label">Vendor</InputLabel>
            <Select
              labelId="vendor-label"
              label="Vendor"
              value={selectedVendorId || ""}
              disabled
              inputProps={{ readOnly: true }}
            >
              <MenuItem value={selectedVendorId || ""}>
                {selectedVendor?.vendorName || selectedVendorId || "Select Vendor"}
              </MenuItem>
            </Select>
            <FormHelperText>Vendor is selected from the parent list</FormHelperText>
          </FormControl>
          <input type="hidden" name="vendorId" value={form.vendorId} />
        </Grid>

        {/* Country Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="Country"
            name="countryId"
            value={form.countryId}
            onChange={handleChange}
            disabled={!selectedVendorId}
          >
            <MenuItem value="" disabled>
              Select Country
            </MenuItem>
            {countries.map(country => (
              <MenuItem key={country.id} value={country.id}>
                {country.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* State Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="State Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Short Code */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Short Code (Optional)"
            name="shortCode"
            value={form.shortCode}
            onChange={handleChange}
          />
        </Grid>

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BoardAddState;